import { History, RotateCcw, RotateCw } from "lucide-react";
import {
	useCallback,
	useEffect,
	useId,
	useMemo,
	useRef,
	useState,
} from "react";
import { useShallow } from "zustand/react/shallow";
import { useMarket, useT } from "../../../i18n";
import { getMarketProfile } from "../../../market/marketProfile";
import {
	type PartializedBuilderState,
	partializeBuilderState,
} from "../../../store/builderStoreConfig";
import {
	useBuilderStore,
	useTemporalStore,
} from "../../../store/useBuilderStore";
import { useUnitSystem } from "../../../units/UnitSystemContext";
import {
	buildHistoryEntries,
	formatHistoryStepLabel,
	type HistoryEntry,
} from "../historyLabels";
import "./BuilderHistoryMenu.css";

/**
 * Readable undo/redo history for the builder toolbar.
 *
 * Everything shown here is derived in render: the labels are memoized over
 * zundo's `pastStates` / `futureStates` and the live partialized state. The
 * panel writes nothing to the store, adds nothing to the history it displays,
 * and persists nothing - the keyboard shortcuts and the plain undo/redo
 * buttons keep working exactly as before.
 *
 * The list body is a separate component so the store subscriptions and the
 * diffing only exist while the popover is open.
 */
export function BuilderHistoryMenu() {
	const t = useT();
	const menuId = useId();
	const [open, setOpen] = useState(false);
	const buttonRef = useRef<HTMLButtonElement | null>(null);
	const wrapperRef = useRef<HTMLDivElement | null>(null);

	const closeAndRefocus = useCallback(() => {
		setOpen(false);
		buttonRef.current?.focus();
	}, []);

	// Pointer outside the popover dismisses it without stealing focus back.
	useEffect(() => {
		if (!open) return;
		const handlePointerDown = (event: MouseEvent | TouchEvent) => {
			const wrapper = wrapperRef.current;
			if (wrapper && !wrapper.contains(event.target as Node)) setOpen(false);
		};
		document.addEventListener("mousedown", handlePointerDown);
		document.addEventListener("touchstart", handlePointerDown);
		return () => {
			document.removeEventListener("mousedown", handlePointerDown);
			document.removeEventListener("touchstart", handlePointerDown);
		};
	}, [open]);

	return (
		<div className="builder-history" ref={wrapperRef}>
			<button
				type="button"
				ref={buttonRef}
				className="btn-tool builder-history__trigger"
				onClick={() => setOpen((previous) => !previous)}
				onKeyDown={(event) => {
					if (event.key === "Escape" && open) {
						event.preventDefault();
						setOpen(false);
					}
				}}
				aria-haspopup="menu"
				aria-expanded={open}
				aria-controls={open ? menuId : undefined}
				aria-label={t.history_buttonLabel}
				title={t.history_buttonTitle}
			>
				<History size={15} strokeWidth={2} aria-hidden="true" />
			</button>
			{open && <BuilderHistoryList id={menuId} onDismiss={closeAndRefocus} />}
		</div>
	);
}

interface BuilderHistoryListProps {
	id: string;
	onDismiss: () => void;
}

function BuilderHistoryList({ id, onDismiss }: BuilderHistoryListProps) {
	const t = useT();
	const market = useMarket();
	const { unitSystem } = useUnitSystem();
	const locale = getMarketProfile(market).locale;

	// zundo types its snapshot arrays as `Partial<T>` so a `diff` option can
	// store deltas. This store configures no `diff`, so every snapshot is a
	// complete partialized state; the assertion is a compile-time no-op.
	//
	// WARNING: zundo MUTATES these arrays in place. `undo` splices `pastStates`
	// and rebuilds `futureStates` with `concat`; `redo` does the mirror image.
	// So after an undo the `pastStates` reference is UNCHANGED even though its
	// contents shrank, and this selector reports no change. The memo below only
	// recomputes because the opposite array (and the partialized state) is
	// always a fresh reference. Never rely on one of these two alone as a
	// dependency, and never treat them as immutable snapshots.
	const pastStates = useTemporalStore(
		(state) => state.pastStates as PartializedBuilderState[],
	);
	const futureStates = useTemporalStore(
		(state) => state.futureStates as PartializedBuilderState[],
	);
	const undo = useTemporalStore((state) => state.undo);
	const redo = useTemporalStore((state) => state.redo);
	const currentState = useBuilderStore(useShallow(partializeBuilderState));

	const entries = useMemo(
		() => buildHistoryEntries({ pastStates, currentState, futureStates }),
		[pastStates, currentState, futureStates],
	);

	const headingId = useId();
	const itemRefs = useRef<(HTMLButtonElement | null)[]>([]);
	const currentIndex = Math.max(
		entries.findIndex((entry) => entry.kind === "current"),
		0,
	);

	// Focus the current step ON OPEN only. Reading the index through a ref keeps
	// a background write (an auto-save status row shifting the list) from
	// yanking focus out from under the user mid-navigation.
	// Captured on the first render and never reassigned: the popover mounts only
	// while open, so "first render" IS "just opened".
	const initialFocusIndexRef = useRef(currentIndex);
	useEffect(() => {
		itemRefs.current[initialFocusIndexRef.current]?.focus();
	}, []);

	const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
		if (event.key === "Escape") {
			event.preventDefault();
			onDismiss();
			return;
		}
		// A popover is not a tab stop the user should get stuck behind: Tab
		// closes it and hands focus back to the trigger, so the next Tab
		// continues through the toolbar in document order.
		if (event.key === "Tab") {
			onDismiss();
			return;
		}
		const items = itemRefs.current.filter(
			(item): item is HTMLButtonElement => item !== null,
		);
		if (items.length === 0) return;
		const active = items.indexOf(document.activeElement as HTMLButtonElement);
		if (event.key === "ArrowDown") {
			event.preventDefault();
			items[active < 0 ? 0 : (active + 1) % items.length]?.focus();
		} else if (event.key === "ArrowUp") {
			event.preventDefault();
			items[active <= 0 ? items.length - 1 : active - 1]?.focus();
		} else if (event.key === "Home") {
			event.preventDefault();
			items[0]?.focus();
		} else if (event.key === "End") {
			event.preventDefault();
			items[items.length - 1]?.focus();
		}
	};

	const handleJump = (entry: HistoryEntry) => {
		if (entry.kind === "past" && entry.steps > 0) undo(entry.steps);
		else if (entry.kind === "future" && entry.steps > 0) redo(entry.steps);
		onDismiss();
	};

	const hasSteps = pastStates.length > 0 || futureStates.length > 0;

	return (
		<div
			id={id}
			className="builder-history__menu"
			role="menu"
			aria-labelledby={headingId}
			onKeyDown={handleKeyDown}
		>
			<p id={headingId} className="builder-history__heading">
				{t.history_menuLabel}
			</p>
			{hasSteps ? (
				// role="none" on the list scaffolding: a `menu` may only own
				// `menuitem`s, so the ul/li must not contribute roles of their own.
				<ul className="builder-history__list" role="none">
					{entries.map((entry, index) => {
						const isCurrent = entry.kind === "current";
						const hint =
							entry.kind === "future"
								? t.history_jumpRedo
								: entry.kind === "past"
									? t.history_jumpUndo
									: t.history_currentStep;
						return (
							<li key={entry.id} role="none">
								<button
									type="button"
									role="menuitem"
									// Roving tabindex: the menu is ONE tab stop.
									tabIndex={index === currentIndex ? 0 : -1}
									ref={(node) => {
										itemRefs.current[index] = node;
									}}
									className="builder-history__item"
									data-kind={entry.kind}
									aria-current={isCurrent ? "step" : undefined}
									title={hint}
									onClick={() => handleJump(entry)}
								>
									<span className="builder-history__icon" aria-hidden="true">
										{entry.kind === "future" ? (
											<RotateCw size={13} strokeWidth={2} />
										) : entry.kind === "past" ? (
											<RotateCcw size={13} strokeWidth={2} />
										) : (
											<span className="builder-history__dot" />
										)}
									</span>
									<span className="builder-history__text">
										{formatHistoryStepLabel(entry.label, t, unitSystem, locale)}
									</span>
									{isCurrent ? (
										<span className="builder-history__badge">
											{t.history_currentBadge}
										</span>
									) : (
										<span className="builder-history__sr">{hint}</span>
									)}
								</button>
							</li>
						);
					})}
				</ul>
			) : (
				<p className="builder-history__empty">{t.history_empty}</p>
			)}
		</div>
	);
}
